import { Button, List, Spin } from 'antd';
import SvgPadlock from '../../../assets/images/padlock';
import React, { useMemo, useRef } from 'react';
import { NewAccountCard, TrialTypes } from './SBPlusTrialModal';
import { costString } from '../../../../../utils/cost';
import SparkleSVG from '../../../assets/images/Sparkle';
import { LoadingOutlined, RightOutlined } from '@ant-design/icons';
import { useGetPlusPricesQuery } from '../../../proPricingSlice';
import { useTrustpilotWidget } from '../../../../../utils/hooks';

export default function SBPlusAdvertCard({
  type = TrialTypes.NO_TRIAL_OR_SUBSCRIPTION,
  userId,
  openModal = () => {},
  guestAccountProps = {},
}) {
  const { data: prices, isLoading: loadingPrices } = useGetPlusPricesQuery(
    undefined,
    { skip: type !== TrialTypes.USER_TRIAL_FINISHED }
  );
  const { proMonthlyPrice, discountedMonthlyPrice } = useMemo(
    () => ({
      proMonthlyPrice: prices?.Pro?.find((p) => p.interval === 'month'),
      discountedMonthlyPrice: prices?.post_trial?.find(
        (p) => p.interval === 'month'
      ),
    }),
    [prices?.Pro, prices?.post_trial]
  );

  const widgetRef = useRef(null);
  useTrustpilotWidget(widgetRef);

  let title;
  let header;
  let content;
  let buttonText;
  let buttonOnClick = () => {};
  switch (type) {
    case TrialTypes.NEW_USER_TRIAL:
    case TrialTypes.NEW_GUEST_TRIAL:
      header = "You've been upgraded!";
      title = 'PLUS Trial';
      content = (
        <>
          <p className='gBold fs-20'>
            We've <span className='seagreen-300'>gifted you 7 days</span> to try{' '}
            <strong className='font-montserrat'>PLUS</strong>!
          </p>
          <p className='gMedium fs-20'>
            We've <strong className='gBold'>upgraded</strong> your swimbooker
            account to swimbooker <strong className='gBold'>PLUS</strong> for
            the next 7 days! You've now got{' '}
            <strong className='gBold'>access to all</strong> of our premium
            features
          </p>
        </>
      );
      buttonText =
        type === TrialTypes.NEW_GUEST_TRIAL && !guestAccountProps?.accountSetupFinished
          ? 'Finish Account Setup'
          : 'Read More';
      buttonOnClick = openModal;
      break;

    case TrialTypes.USER_TRIAL_IN_PROGRESS:
      header = "Don't forget!";
      title = "You've been upgraded!";
      content = (
        <>
          <p className='gBold fs-20'>
            Download the app for the best experience
          </p>
          <p className='gMedium fs-20'>
            Your swimbooker <strong className='gBold'>PLUS</strong> upgrade is
            still active! Click the button below to access all of your premium
            features!
          </p>
        </>
      );
      buttonText = 'Read More';
      buttonOnClick = openModal;
      break;

    case TrialTypes.USER_TRIAL_FINISHED:
      header = 'Claim your lifetime discount';
      title = null;
      content = (
        <>
          <p className='gBold fs-24 mg-bottom-10'>
            Thanks for trying <strong className='font-montserrat'>PLUS!</strong>
          </p>
          <p className='font-montserrat fs-16'>
            YOUR EXCLUSIVE AFTER TRIAL OFFER
          </p>
          <div className='mg-bottom-10 display-flex-row flex-wrap row-center justify-center gap-1r row-gap-0 font-montserrat fs-26 white'>
            {loadingPrices ? (
              <Spin
                indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
              />
            ) : (
              <>
                <span
                  className='fs-24'
                  style={{
                    textDecorationLine: 'line-through',
                    textDecorationColor: '#8ABCE5',
                    textDecorationThickness: 2,
                  }}
                >
                  &nbsp;
                  {costString(
                    proMonthlyPrice?.cost ? proMonthlyPrice.cost / 100.0 : null
                  )}
                  &nbsp;p/m&nbsp;
                </span>
                <span>
                  {costString(
                    discountedMonthlyPrice?.cost
                      ? discountedMonthlyPrice.cost / 100.0
                      : null
                  )}
                  &nbsp;p/m
                  <span className='brFull fs-12 pd-left-10 pd-right-10 border-1 uppercase mg-left-12 va-middle sb-blue-bg-600'>
                    your offer
                  </span>
                </span>
              </>
            )}
          </div>
          <div className='bg-bright-gradient br16 pd-2 max-w-400 mg-auto-lr'>
            <Button
              href={`${process.env.REACT_APP_SBPLUS_URL}/sign-up/post-trial-offer?interval=month&userId=${userId}`}
              className='h-full w-full display-flex-row row-center txt-wrap br14 border-0 bg-black gRefular fs-18 white pd-15 antd-link-button-override'
            >
              <div className='display-flex-row row-center justify-center flex-grow-1 mg-top-5'>
                <SparkleSVG className='mg-right-20 fs-30' />
                <span className='lh-0'>
                  Join{' '}
                  <strong className='font-montserrat uppercase'>Plus</strong>{' '}
                  Now!
                </span>
                <RightOutlined className='mg-left-auto' />
              </div>
            </Button>
          </div>
          <div
            key='trustpilot-widget'
            ref={widgetRef}
            className='trustpilot-widget gRegular display-flex-row mg-top-30'
            data-locale='en-US'
            data-template-id='5419b732fbfb950b10de65e5'
            data-font-family='gRegular'
            data-businessunit-id='61657394fa7737001e35bc8a'
            data-style-height='24px'
            data-style-width='100%'
            data-theme='dark'
            data-stars='4,5'
            data-review-languages='en'
          >
            <a
              href='https://uk.trustpilot.com/review/www.swimbooker.com'
              target='_blank'
              rel='noopener'
            >
              Trustpilot
            </a>
          </div>
          <Button
            type='text'
            className='white gSemiBold fs-20  mg-top-20'
            onClick={openModal}
          >
            <span className='txt-underline'>Click for full details</span>
          </Button>
        </>
      );
      buttonText = null;
      break;

    case TrialTypes.NO_TRIAL_OR_SUBSCRIPTION:
      header = 'Welcome back!';
      title = 'Want more?';
      content = (
        <>
          <div className='gSemiBold fs-16'>
            <List
              bordered={false}
              className='w-fit mg-auto-lr'
              size='small'
              dataSource={[
                'Advanced Catch Report Data',
                'Comprehensive Angling Insurance',
                'Unique Products + Weekly Giveaways',
                'Cashback On Every Booking!',
              ]}
              renderItem={(item) => (
                <List.Item
                  className={
                    'gSemiBold fs-17 txt-left pd-top-5 pd-bottom-10 white'
                  }
                  style={{ borderBottom: 'none' }}
                >
                  <SvgPadlock className='seagreen-200 mg-right-5' /> {item}
                </List.Item>
              )}
            />
          </div>
        </>
      );
      buttonText = 'Learn More';
      buttonOnClick = () =>
        window.open(`${process.env.REACT_APP_SBPLUS_URL}/sign-up`, '_blank');

      break;

    case TrialTypes.PAID_SUBSCRIPTION:
      header = 'Welcome back!';
      title = 'SB+ Member';
      content = (
        <>
          <p className='gMedium fs-16'>Thanks for being a valued subscriber!</p>
          <div className='gSemiBold fs-16'>
            <List
              bordered={false}
              className='w-fit mg-auto-lr'
              size='small'
              dataSource={[
                "Don't forget to enter the giveaways!",
                'You are covered in case of accidents or damage to equipment!',
                'Keep an eye on the perks section!',
              ]}
              renderItem={(item) => (
                <List.Item
                  className={
                    'gSemiBold fs-17 txt-left pd-top-5 pd-bottom-10 white'
                  }
                  style={{ borderBottom: 'none' }}
                >
                  <SvgPadlock className='seagreen-200 mg-right-5' /> {item}
                </List.Item>
              )}
            />
          </div>
        </>
      );
      buttonText = 'View MySB+';
      buttonOnClick = () => window.open('/user/profile', '_blank');
      break;

    case TrialTypes.GUEST_NO_TRIAL:
      return <NewAccountCard userId={userId} {...guestAccountProps} useTrialConfirmation={false} />;

    default:
      header = null;
      content = null;
      title = null;
      buttonText = null;
  }

  return (
    <div
      className={'br20 grey-bg-900 white txt-center overflow-hidden'}
      id={'plus-banner'}
    >
      <div className='uppercase gBold fs-21 pd-10 grey-100 sb-blue-bg-500'>
        {header}
      </div>
      <div className={'pd-20 pd-top-30 pd-bottom-30'}>
        {title && (
          <h2
            className={`font-montserrat white mg-bottom-5 pd-bottom-8 mg-top-0 ${
              type === TrialTypes.USER_TRIAL_IN_PROGRESS ? 'fs-28' : 'fs-30'
            }`}
          >
            {title}
          </h2>
        )}
        {content}
        {buttonText && (
          <Button
            onClick={buttonOnClick}
            className='sb-blue-gradient-bg-right border-1 sb-blue-border-300 mg-top-30 white gSemiBold brFull fs-18 txt-center w-full max-w-300 pd-10 h-fit txt-wrap antd-link-button-override'
          >
            {buttonText}
          </Button>
        )}
      </div>
    </div>
  );
}
